import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { faCartShopping } from "@fortawesome/pro-solid-svg-icons"
import { Box, Drawer, Grid, Stack, Tooltip } from "@mui/material"
import { useField, useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { IconBadge } from "../../badges/IconBadge"
import { ButtonConfigType, StandardOrderTypeFilter } from "../../filters/orderTypeFilter/StandardOrderTypeFilter"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { MeetingServicePriceListState } from "../createOrderTypes"
import { CreateOrderType } from "../helpers"
import { useCreateOrderDialogType } from "../hooks/useCreateOrderDialogType"
import { OrderFormStatus } from "../mainForm/helpers"
import { useCreateOrderForm } from "../mainForm/CreateOrderFormContext"
import { CreateOrderDialogTitle } from "./CreateOrderDialogTitle"
import { OrderSummaryWrap } from "./drawer/orderSummary/OrderSummaryWrap"
import { DRAWER_WIDTH } from "./drawer/DrawerContent"
import { DeliveryTimesField, DynamicConfigKey, DynamicMainFormValues } from "./form/formTypes"
import { MEETING_SERVICE_FORM_ID } from "./meetingService/helpers"
import { ResourceBookingControls } from "./resource/ResourceBookingControls"
import { useResourceBookingDraft } from "./resource/ResourceBookingDraftContext"
import { PriceListEditButton } from "./shop/priceLists/PriceListEditButton"
import { ConfirmOrderSubmitButton } from "./ConfirmOrderSubmitButton"

const useStyles = makeStyles()((_theme) => ({
    drawer: {
        zIndex: "9999",
    },
    container: {
        overflow: "auto",
    },
    drawerContent: {
        flexGrow: 1,
        padding: "1rem",
        justifyContent: "space-between",
        width: DRAWER_WIDTH,
    },
    drawerCart: {
        marginRight: "0.75rem",
    },
    drawerTitle: {
        maxWidth: "24.5rem",
    },
}))

type CreateOrderDialogTitleWrapProps = Readonly<{
    orderType: CreateOrderType
    handleChangeOrderType: (type: CreateOrderType) => void
    filteredTabButtonConfig: ButtonConfigType<CreateOrderType>[]
    confirmBookingDialog: boolean
    setConfirmBookingDialog: Dispatch<SetStateAction<boolean>>
    handleOpenSelectPriceListsDialog: () => void
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
}>

export function CreateOrderDialogTitleWrap({
    orderType,
    handleChangeOrderType,
    filteredTabButtonConfig,
    confirmBookingDialog,
    setConfirmBookingDialog,
    handleOpenSelectPriceListsDialog,
    orderFormStatusRef,
}: CreateOrderDialogTitleWrapProps) {
    const { user, handleClose, dialogType, flowConfig, orderContext } = useCreateOrderForm()
    const { classes } = useStyles()
    const { dirty } = useFormikContext<DynamicMainFormValues>()
    const { isRefundOrder, isRegularOrder } = useCreateOrderDialogType(dialogType)

    const [{ value: deliveries }] = useField<DeliveryTimesField | undefined>(DynamicConfigKey.MULTIPLE_DELIVERIES)
    const [{ value: priceListState }] = useField<MeetingServicePriceListState>("priceList")
    const priceListItem: MeetingServicePriceListState["item"] = priceListState.item
    const { draftEvent, syncDraftToFormik } = useResourceBookingDraft()

    const [drawerOpen, setDrawerOpen] = useState(false)
    const hasAutoOpenedRef = useRef(false)

    useEffect(() => {
        const hasDeliveries = !!deliveries?.deliveryTimes?.length

        if (!hasAutoOpenedRef.current && isRefundOrder && hasDeliveries) {
            setDrawerOpen(true)
            hasAutoOpenedRef.current = true
        }
    }, [isRefundOrder, deliveries])

    const resourcesCount = draftEvent?.resourceIds.length || 0

    const deliveriesCount = useMemo(() => {
        if (!deliveries?.deliveryTimes?.length) return 0

        return deliveries.deliveryTimes.reduce((acc, slot) => {
            const lines = slot?.orderLines ?? []
            const sumInSlot = lines.reduce((s, line) => s + (Number(line?.quantity) || 0), 0)
            return acc + sumInSlot
        }, 0)
    }, [deliveries])

    const badgeCount = resourcesCount + deliveriesCount

    const handleBookClick = useCallback(() => {
        syncDraftToFormik()
        handleCloseDrawer()
        setConfirmBookingDialog(true)
    }, [syncDraftToFormik, setConfirmBookingDialog])

    const handleDrawerOpen = () => {
        setDrawerOpen(true)
    }

    const handleCloseDrawer = () => {
        setDrawerOpen(false)
    }

    return (
        <>
            <CreateOrderDialogTitle handleClose={handleClose} isDirtyForm={dirty} flowConfig={flowConfig}>
                <Grid container flexWrap="nowrap" alignItems="center" flexDirection="row" gap="1rem">
                    <StandardOrderTypeFilter
                        orderType={orderType}
                        setOrderType={handleChangeOrderType}
                        config={filteredTabButtonConfig}
                        isMarginBottom={false}
                        isLight={false}
                    />
                    {orderType === CreateOrderType.MEETING_SERVICE && priceListItem && (
                        <PriceListEditButton
                            selectedPriceList={priceListItem}
                            handleOpenSelectPriceListsDialog={handleOpenSelectPriceListsDialog}
                            orderContext={orderContext}
                            isDisabledEdit={!isRegularOrder}
                        />
                    )}
                </Grid>
                <LocalizedStrict id="create-order-tooltip-order-summary-button" attrs={{ title: true }}>
                    <Tooltip title="View order summary">
                        <div>
                            <IconBadge
                                variant="font-awesome"
                                color="blue"
                                badgeValue={badgeCount}
                                fontAwesomeIcon={faCartShopping}
                                className={classes.drawerCart}
                                onClick={handleDrawerOpen}
                            />
                        </div>
                    </Tooltip>
                </LocalizedStrict>
            </CreateOrderDialogTitle>
            {drawerOpen && (
                <Drawer
                    anchor="right"
                    variant={isRefundOrder ? "persistent" : "temporary"}
                    open={drawerOpen}
                    onClose={handleCloseDrawer}
                    className={classes.drawer}
                    // ModalProps={isRefundOrder ? { BackdropProps: { invisible: true } } : undefined}
                >
                    <Stack gap="1.5rem" className={classes.drawerContent}>
                        <Box className={classes.container}>
                            <OrderSummaryWrap
                                isRefundOrder={isRefundOrder}
                                handleCloseDrawer={handleCloseDrawer}
                                isUpdatable={orderType === CreateOrderType.RESOURCE_BOOKING}
                                orderType={orderType}
                                userId={user.userId}
                                mode={orderContext.mode}
                            />
                        </Box>
                        {orderType === CreateOrderType.RESOURCE_BOOKING && (
                            <ResourceBookingControls
                                onBook={handleBookClick}
                                isFixed={false}
                                isLoadingButton={confirmBookingDialog}
                                flow={flowConfig?.flow}
                                orderFormStatusRef={orderFormStatusRef}
                            />
                        )}
                        {isRefundOrder && orderType === CreateOrderType.MEETING_SERVICE && (
                            <ConfirmOrderSubmitButton
                                isRefundOrder={isRefundOrder}
                                formId={MEETING_SERVICE_FORM_ID}
                                handleSubmit={handleCloseDrawer}
                            />
                        )}
                    </Stack>
                </Drawer>
            )}
        </>
    )
}
